import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@v2/lib/utils';

const switchVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-neutral-500',
  {
    variants: {
      variant: {
        rounded: 'rounded-full',
        squared: 'rounded-[3px]'
      },
      size: {
        xs: 'h-3.5 w-4',
        sm: 'h-5 w-9',
        md: 'h-6 w-11',
        xl: 'h-7 w-12',
        '2xl': 'h-8 w-14'
      }
    },
    defaultVariants: {
      variant: 'rounded',
      size: 'md'
    }
  }
);

const thumbVariants = cva(
  'pointer-events-none block bg-background shadow-lg ring-0 transition-transform',
  {
    variants: {
      variant: {
        rounded: 'rounded-full',
        squared: 'rounded-[1.5px]'
      },
      size: {
        xs: 'h-[11px] w-1.5 data-[state=checked]:translate-x-1.5 data-[state=unchecked]:translate-x-0',
        sm: 'h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
        md: 'h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
        xl: 'h-6 w-6 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0',
        '2xl':
          'h-7 w-7 data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-0'
      }
    },
    defaultVariants: {
      variant: 'rounded',
      size: 'md'
    }
  }
);

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {
  label?: string;
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, variant, size, label, ...props }, ref) => {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: <explanation>
    <label className="flex cursor-pointer items-center gap-2">
      <SwitchPrimitives.Root
        className={cn(switchVariants({ variant, size }), className)}
        {...props}
        ref={ref}
      >
        <SwitchPrimitives.Thumb
          className={cn(thumbVariants({ variant, size }))}
        />
      </SwitchPrimitives.Root>
      {label && <span className="text-neutral-1000 text-sm">{label}</span>}
    </label>
  );
});

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
